import React, { useEffect, useState, useCallback } from 'react';
import DealerLayout from '../components/DealerLayout';
import api from '../api/axios';
import { C } from '../theme';

const BLANK = { client_name: '', purpose: 'own', company_name: '', contact: '', email: '', gst_number: '', address: '' };

export default function DealerClients() {
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search,  setSearch]    = useState('');
  const [modal,   setModal]     = useState(null);   // 'add' | 'edit' | 'delete'
  const [selC,    setSelC]      = useState(null);
  const [form,    setForm]      = useState({ ...BLANK });
  const [errs,    setErrs]      = useState({});
  const [saving,  setSaving]    = useState(false);
  const [toast,   setToast]     = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/dealer/clients');
      setClients(r.data);
    } catch { showToast('error', 'Failed to load clients.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const f = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: '' })); };

  const openAdd = () => { setForm({ ...BLANK }); setErrs({}); setSelC(null); setModal('add'); };
  const openEdit = (c) => {
    setForm({
      client_name: c.client_name || '', purpose: c.purpose || 'own',
      company_name: c.company_name || '', contact: c.contact || '',
      email: c.email || '', gst_number: c.gst_number || '', address: c.address || '',
    });
    setErrs({}); setSelC(c); setModal('edit');
  };
  const openDelete = (c) => { setSelC(c); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelC(null); setErrs({}); };

  const validate = () => {
    const e = {};
    if (!form.client_name.trim()) e.client_name = 'Name is required.';
    if (!form.contact.trim())     e.contact      = 'Contact is required.';
    if (form.purpose === 'company' && !form.company_name.trim()) e.company_name = 'Company name is required.';
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        client_name:  form.client_name.trim(),
        purpose:      form.purpose,
        company_name: form.company_name.trim() || null,
        contact:      form.contact.trim(),
        email:        form.email.trim()       || null,
        gst_number:   form.gst_number.trim()  || null,
        address:      form.address.trim()     || null,
      };
      if (selC) await api.put(`/api/dealer/clients/${selC.id}`, body);
      else      await api.post('/api/dealer/clients', body);
      await load();
      showToast('success', selC ? 'Client updated.' : 'Client added.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const deleteClient = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/dealer/clients/${selC.id}`);
      await load();
      showToast('success', 'Client deleted.');
      closeModal();
    } catch { showToast('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return !q || c.client_name?.toLowerCase().includes(q) || c.contact?.toLowerCase().includes(q) || c.client_id?.toLowerCase().includes(q) || c.company_name?.toLowerCase().includes(q);
  });

  return (
    <DealerLayout title="My Clients">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color: toast.type === 'success' ? C.successText : C.dangerText,
          border: `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <StatChip label="Total Clients" val={clients.length}                                            color="#6D28D9" bg="#F5F3FF" />
          <StatChip label="Active"        val={clients.filter(c=>c.status==='active').length}             color="#059669" bg={C.successBg} />
          <StatChip label="Inactive"      val={clients.filter(c=>c.status!=='active').length}             color={C.danger} bg={C.dangerBg} />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search clients…"
            style={{ padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', width: '220px' }}
          />
          <button onClick={openAdd} style={btnPurple}>+ Add Client</button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.textSub }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>👥</div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>No Clients Yet</div>
          <div style={{ fontSize: '13px', color: C.textSub }}>Add your first client to get started.</div>
        </div>
      ) : (
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                  {['Client ID', 'Name', 'Contact', 'Type', 'Status', 'Added', 'Actions'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? C.white : C.primaryBg, borderBottom: `1px solid ${C.border}` }}>
                    <td style={td}><span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#6D28D9', fontSize: '12px' }}>{c.client_id}</span></td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: C.textMain }}>{c.client_name}</div>
                      {c.company_name && <div style={{ fontSize: '11px', color: C.textSub }}>{c.company_name}</div>}
                    </td>
                    <td style={td}><span style={{ fontSize: '12px' }}>{c.contact}</span></td>
                    <td style={td}>
                      <span style={{ background: c.purpose === 'company' ? '#EDE9FE' : '#FEF3C7', color: c.purpose === 'company' ? '#6D28D9' : '#92400E', padding: '2px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>
                        {c.purpose === 'company' ? 'Company' : 'Individual'}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ background: c.status === 'active' ? C.successBg : C.dangerBg, color: c.status === 'active' ? '#059669' : C.danger, padding: '2px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>
                        {c.status === 'active' ? '● Active' : '○ Inactive'}
                      </span>
                    </td>
                    <td style={td}><span style={{ fontSize: '12px', color: C.textSub }}>{c.created_at}</span></td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => openEdit(c)} style={btnEdit}>Edit</button>
                        <button onClick={() => openDelete(c)} style={btnDel}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      {(modal === 'add' || modal === 'edit') && (
        <Overlay>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>
            <div style={{ background: 'linear-gradient(135deg,#3B0764,#1E1040)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{selC ? '✏️ Edit Client' : '+ Add Client'}</div>
              <button onClick={closeModal} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>✕</button>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <Field label="Client Name *" error={errs.client_name}>
                  <input value={form.client_name} onChange={e => f('client_name', e.target.value)} style={inp(errs.client_name)} placeholder="Full name" />
                </Field>
                <Field label="Type">
                  <select value={form.purpose} onChange={e => f('purpose', e.target.value)} style={inp()}>
                    <option value="own">Individual</option>
                    <option value="company">Company</option>
                  </select>
                </Field>
              </div>
              {form.purpose === 'company' && (
                <Field label="Company Name *" error={errs.company_name}>
                  <input value={form.company_name} onChange={e => f('company_name', e.target.value)} style={inp(errs.company_name)} placeholder="Company name" />
                </Field>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <Field label="Contact *" error={errs.contact}>
                  <input value={form.contact} onChange={e => f('contact', e.target.value)} style={inp(errs.contact)} placeholder="Phone number" />
                </Field>
                <Field label="Email">
                  <input value={form.email} onChange={e => f('email', e.target.value)} style={inp()} placeholder="email@example.com" type="email" />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <Field label="GST Number">
                  <input value={form.gst_number} onChange={e => f('gst_number', e.target.value)} style={inp()} placeholder="GST number" />
                </Field>
              </div>
              <Field label="Address">
                <textarea value={form.address} onChange={e => f('address', e.target.value)} style={{ ...inp(), height: '70px', resize: 'vertical' }} placeholder="Full address" />
              </Field>
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={closeModal} style={btnGhost}>Cancel</button>
              <button onClick={save} disabled={saving} style={btnPurple}>{saving ? 'Saving…' : selC ? 'Update Client' : 'Add Client'}</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Delete modal */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{ background: C.white, borderRadius: '14px', width: '100%', maxWidth: '400px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '22px 24px' }}>
              <div style={{ fontSize: '36px', textAlign: 'center', marginBottom: '14px' }}>🗑️</div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: C.textMain, textAlign: 'center', marginBottom: '8px' }}>Delete Client</div>
              <div style={{ fontSize: '13px', color: C.textSub, textAlign: 'center' }}>
                Delete <strong>{selC?.client_name}</strong>? This cannot be undone.
              </div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} style={btnGhost}>Cancel</button>
              <button onClick={deleteClient} disabled={saving} style={{ ...btnPurple, background: C.danger }}>{saving ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </Overlay>
      )}
    </DealerLayout>
  );
}

function StatChip({ label, val, color, bg }) {
  return (
    <div style={{ background: bg, border: `1px solid ${color}30`, borderRadius: '10px', padding: '10px 16px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 800, color }}>{val}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>{label}</label>
      {children}
      {error && <div style={{ fontSize: '11px', color: C.danger, marginTop: '4px' }}>{error}</div>}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      {children}
    </div>
  );
}

const th = { textAlign: 'left', padding: '10px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const inp = (err) => ({ width: '100%', padding: '9px 12px', boxSizing: 'border-box', border: `1.5px solid ${err ? C.danger : C.border}`, borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', background: C.white });
const btnPurple = { padding: '10px 20px', background: 'linear-gradient(135deg,#7C3AED,#5B21B6)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
const btnGhost  = { padding: '10px 16px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnEdit   = { padding: '5px 12px', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' };
const btnDel    = { padding: '5px 12px', background: C.dangerBg, color: C.danger, border: `1px solid #FECACA`, borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' };
